import { useEffect } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'

import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { AccountForm } from '@/components/form/AccountForm'
import type { AccountFormValues } from '@/components/form/AccountForm'
import {
  createAccount,
  updateAccount,
  getAccountById,
} from '@/api/accounts.api'
import type { Account } from '@/types/api'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'

interface AccountFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  accountId: string | null
  onClose: () => void
}

export function AccountFormDrawer({
  isOpen,
  mode,
  accountId,
  onClose,
}: AccountFormDrawerProps) {
  const [form] = Form.useForm<AccountFormValues>()
  const queryClient = useQueryClient()
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const { data: account, isLoading: isFetchingAccount } = useQuery({
    queryKey: ['account-detail', accountId],
    queryFn: ({ signal }) =>
      accountId ? getAccountById(accountId, signal) : Promise.resolve(null),
    enabled: isOpen && mode === 'update' && !!accountId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen) {
      if (mode === 'update' && account) {
        form.setFieldsValue({
          id: account.id,
          username: account.username,
          fullName: account.fullName,
          email: account.email,
          phone: account.phone,
          note: account.note,
          unitId: showUnitColumn ? account.unitId : currentUser?.unitId,
          groupPermissionId: account.groupPermissionId,
          groupPermissionName: account.groupPermissionName,
          roleName: account.roleName,
        })
      } else if (mode === 'create') {
        form.resetFields()
      }
    } else {
      form.resetFields()
    }
  }, [isOpen, mode, account, form, showUnitColumn, currentUser])

  const createMutation = useMutation({
    mutationFn: createAccount,
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Thêm mới tài khoản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: Partial<AccountFormValues>) => {
      if (!accountId) throw new Error('Missing account ID')
      return updateAccount(accountId, {
        ...values,
        status: account?.status ?? 1,
      })
    },
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Cập nhật tài khoản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isFetching = isFetchingAccount
  const isDrawerDisabled = isSubmitting || isFetching

  const handleSubmit = (values: AccountFormValues) => {
    if (mode === 'create') {
      const payload: Omit<Account, 'id' | 'createdAt' | 'updatedAt'> = {
        username: values.username || values.email,
        fullName: values.fullName,
        email: values.email,
        phone: values.phone ?? '',
        note: values.note,
        status: 1,
        unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
        groupPermissionId: values.groupPermissionId,
      }
      createMutation.mutate(payload)
    } else {
      updateMutation.mutate({
        ...(account || {}),
        username: values.username,
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
        note: values.note,
        unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
        groupPermissionId: values.groupPermissionId,
      })
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI TÀI KHOẢN' : 'CẬP NHẬT TÀI KHOẢN'}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin spinning={isFetching} description="Đang tải dữ liệu...">
        <div className="min-h-[300px]">
          {(mode === 'create' || account) && (
            <AccountForm
              form={form}
              mode={mode}
              showUnitColumn={showUnitColumn}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </Spin>
    </Drawer>
  )
}
